import * as React from "react";
import { Loader } from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useAutoOpenTaskpane } from "../hooks/useAutoOpenTaskpane";

const Intro = React.lazy(() => import("./Intro"));
const AuthFlow = React.lazy(() => import("./auth"));
const AuthenticatedScreen = React.lazy(() => import("./AuthenticatedScreen"));

const App: React.FC = () => {
  const { isAuthenticated, isLoading, isError } = useAuth();
  const [showLogin, setShowLogin] = React.useState(true);
  const previousIsAuthenticated = React.useRef(isAuthenticated);

  // Configure document to auto-open this task pane next time
  useAutoOpenTaskpane();


  React.useEffect(() => {
    if (previousIsAuthenticated.current && !isAuthenticated) {
      setShowLogin(true);
    }
    previousIsAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  const handleGetStarted = () => {
    setShowLogin(true);
  };

  return (
    <div
      className="flex flex-col h-screen"
      role="application"
      aria-label="Legal Assistant AI Add-in"
    >
      {/* Conditionally render content based on loading state */}
      {isLoading ? (
        // Show loading spinner while authentication status is being determined
        <div className="flex flex-col items-center justify-center h-screen gap-4">
          <Loader className="animate-spin" size={48} />
          <p>Loading...</p>
        </div>
      ) : (
        // Once loading is complete, render main application content
        <main id="main-content" className="h-full" role="main">
          {/* Conditionally render based on authentication status or error */}
          <React.Suspense
            fallback={
              <div className="flex flex-col items-center justify-center h-screen gap-4">
                <Loader className="animate-spin" size={48} />
                <p>Loading...</p>
              </div>
            }
          >
            {!isAuthenticated || isError ? (
              // If not authenticated or an error occurred, show login or intro screen
              <div className="py-4 px-4">
                {showLogin ? <AuthFlow /> : <Intro onGetStarted={handleGetStarted} />}
              </div>
            ) : (
              // If authenticated, show the main authenticated screen
              <AuthenticatedScreen />
            )}
          </React.Suspense>
        </main>
      )}
    </div>
  );
};

export default App;
